<template>
  <div
    :class="$style.modalContainer"
  >
    <StepHeader
      :class="$style.header"
      isVerifyStep
    />
    <ConfirmHead />
    <section
      v-if="confirming"
      :class="[$style.content, $style.contentCenter]"
      data-test-id="ConfirmPartner__verifySection"
    >
      <HeadingLarge
        :class="$style.heading"
        data-test-id="ConfirmPartner__verifyHeading"
      >
        Verifying Your Account...
      </HeadingLarge>
      <TransitionWrapper name="fade">
        <div>
          <ParagraphBody
            v-if="feedback.text"
            :class="{ [$style.errorText]: feedback.isTextRed }"
          >
            <LoadingIcon
              v-if="feedback.loading"
              :class="$style.loadingIcon"
              inline
            />
            <Icon
              v-if="feedback.error"
              icon="error"
              inline
            />
            {{ feedback.text }}
          </ParagraphBody>
          <Button
            v-if="feedback.linkText"
            data-test-id="ConfirmPartner__resendButton"
            hasOutline
            isMarketingStyle
            :text="feedback.linkText"
            @click="resendConfirmation"
          />
          <ParagraphBody
            v-if="needsSupport"
            :class="$style.help"
          >
            Need some help?
            <Link
              href="https://jumpcloud.com/support"
              isExternal
              text="Contact support"
            />
          </ParagraphBody>
        </div>
      </TransitionWrapper>
    </section>
    <section
      v-else
      :class="$style.content"
      data-test-id="ConfirmPartner__formSection"
    >
      <HeadingLarge
        :class="$style.heading"
        data-test-id="ConfirmPartner__heading"
      >
        Welcome to JumpCloud!
      </HeadingLarge>
      <ParagraphBody
        bodySize="large"
        :class="$style.subheading"
      >
        Tell us a little about you
      </ParagraphBody>
      <ValidationObserver
        v-slot="{ handleSubmit, disabled }"
        isDisabled
        novalidate
        slim
      >
        <form
          @keydown.enter.prevent="handleSubmit(submitConfirmRegistration)"
          @submit.prevent.stop="handleSubmit(submitConfirmRegistration)"
        >
          <PartnerPicker dataTestIdPrefix="ConfirmPartner" />
          <Select
            v-model="user.companySize"
            :class="$style.selectSize"
            data-test-id="ConfirmPartner__companySizeSelect"
            label="Company Size"
            large
            name="company size"
            :options="$options.companySizeOptions"
            rules="required"
          />
          <CountryPicker />
          <Button
            data-test-id="ConfirmPartner__submitButton"
            :isDisabled="disabled || invalidCountrySelected"
            :isLoading="false"
            isFullWidth
            isMarketingStyle
            text="Continue"
          />
        </form>
      </ValidationObserver>
    </section>
    <Footer
      :class="$style.footer"
    />
  </div>
</template>

<script>
import {
  Button,
  HeadingLarge,
  Icon,
  Link,
  LoadingIcon,
  ParagraphBody,
  Select,
  TransitionWrapper,
} from '@jumpcloud/ui-components';
import {
  LocalStorageConsts,
} from 'client/util/Constants';
import { ValidationObserver } from 'vee-validate';
import { mapActions, mapState } from 'vuex';
import ConfirmHead from 'client/signup/components/ConfirmHead.vue';
import CountryPicker from 'client/signup/components/inputs/CountryPicker.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import Footer from 'client/signup/components/Footer.vue';
import PartnerPicker from 'client/signup/components/inputs/PartnerPicker.vue';
import SignupApi from 'client/signup/api/SignupApi';
import SignupConfirmationMessages from 'client/signup/SignupConfirmationMessages';
import SignupConstants from 'client/util/Constants/SignupConstants';
import SignupOptions from 'client/signup/SignupOptions';
import StepHeader from 'client/signup/components/StepHeader.vue';

const { signupDisabledCountries, partnershipNames } = SignupConstants;
const { partnerMarketing } = LocalStorageConsts;

export default {
  name: 'ConfirmPartner',

  components: {
    Button,
    ConfirmHead,
    CountryPicker,
    Footer,
    HeadingLarge,
    Icon,
    Link,
    LoadingIcon,
    ParagraphBody,
    PartnerPicker,
    Select,
    StepHeader,
    TransitionWrapper,
    ValidationObserver,
  },

  data: () => ({
    code: '',
    confirming: false,
    emailAddress: '',
    feedback: {
      error: false,
      isTextRed: false,
      loading: false,
      showResendEmailLink: false,
      text: '',
      linkText: '',
    },
    hasFailed: false,
    needsSupport: false,
    resendEmailInstructionsVisible: false,
  }),

  computed: {
    ...mapState([
      'user',
    ]),

    invalidCountrySelected() {
      return signupDisabledCountries.indexOf(this.user.country) !== -1;
    },
  },

  created() {
    this.getQueries();

    // check session storage for previous responses and resubmit what was already input
    const sessionData = JSON.parse(sessionStorage.getItem(partnerMarketing));
    if (sessionData && sessionData.email === this.emailAddress) {
      this.updatePartnerResponses(this.emailAddress);
      this.submitConfirmRegistration();
    } else {
      try {
        sessionStorage.removeItem(partnerMarketing);
      } catch (e) {
        // Do nothing, just catch for safety and move on
      }
    }
  },

  // Options
  companySizeOptions: SignupOptions.companySizeOptions,

  methods: {
    ...mapActions([
      'confirmPartnerRegistration',
      'setValidation',
      'updatePartnerResponses',
    ]),

    getQueries() {
      const { query } = this.$route;
      // The email address should be passed in the query
      // string and is needed to confirm registration
      this.emailAddress = query.email;
      this.code = query.registrationCode;
    },

    resendConfirmation() {
      if (this.feedback.error) {
        EventHelper.triggerResendEmailAfterError();
      } else {
        EventHelper.triggerResendEmailBeforeError();
      }

      this.updateFeedbackText({
        loading: true,
        text: SignupConfirmationMessages.resending,
      });

      return SignupApi.resendRegistrationCode(this.emailAddress, partnershipNames.crowdStrike)
        .then(() => (
          this.updateFeedbackText({ text: SignupConfirmationMessages.resent })
        ))
        .catch(() => {
          this.updateFeedbackText({
            isTextRed: true,
            text: SignupConfirmationMessages.resendError,
            linkText: SignupConfirmationMessages.tryAgain,
          });

          this.needsSupport = true;
        });
    },

    submitConfirmRegistration() {
      if (this.invalidCountrySelected) return false;

      this.setValidation(true);
      this.confirming = true;

      this.updateFeedbackText({
        loading: true,
        text: SignupConfirmationMessages.verifying,
      });

      return this.confirmPartnerRegistration({ emailAddress: this.emailAddress, code: this.code })
        .catch((error) => {
          let feedbackText;
          let linkText;

          this.confirming = false;

          if (error && error.response && error.response.data.message.indexOf('Expired') >= 0) {
            feedbackText = SignupConfirmationMessages.expired;
            linkText = SignupConfirmationMessages.expiredLinkText;
          } else {
            feedbackText = SignupConfirmationMessages.invalid;
            linkText = SignupConfirmationMessages.invalidLinkText;
          }

          this.updateFeedbackText({
            error: true,
            isTextRed: true,
            showResendEmailLink: true,
            text: feedbackText,
            linkText,
          });
        });
    },

    updateFeedbackText(feedbackObj = {}) {
      this.feedback = {
        error: false,
        isTextRed: false,
        loading: false,
        showResendEmailLink: false,
        text: '',
        linkText: '',
        ...feedbackObj,
      };
      return this.feedback;
    },
  },
};
</script>

<style module scoped>
  .modalContainer {
    align-items: center;
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
    min-height: 42rem;
  }

  .content {
    max-width: 25rem;
    padding: var(--jc-spacer-medium) var(--jc-spacer) 0;
    width: 100%;
  }

  .contentCenter {
    text-align: center;
  }

  .heading {
    font-family: var(--serifFont);
  }

  .heading,
  .subheading {
    text-align: center;
  }

  .errorText {
    color: var(--jcError);
  }

  .loadingIcon {
    color: var(--jcPrimary);
  }

  .help.help {
    margin-top: var(--jc-spacer);
  }

  .footer {
    padding: var(--jc-spacer-medium) 0;
  }
</style>
